import React from 'react';
import LeftArrowDoubleIcon from '../icons/LeftArrowDoubleIcon';
import LeftArrowIcon from '../icons/LeftArrowIcon';
import RightArrowIcon from '../icons/RightArrowIcon';
import RightArrowDoubleIcon from '../icons/RightArrowDoubleIcon';

/**
 * 목록 아래 쪽 번호 ( « ‹ 1 2 3 › » ). 사용법은 docs/ui-components.md 참고.
 * 감싸는 여백(padding 등)은 style로 준다.
 */
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onChange: (page: number) => void;
  /** 맨 앞·맨 뒤로 가는 « » 버튼. 기본 true */
  showFirstLast?: boolean;
  style?: React.CSSProperties;
}

const TEXT_ACTIVE = 'var(--Color-text-normal, #161616)';
const TEXT_INACTIVE = 'var(--Color-text-assistive, #989898)';

const ArrowButton: React.FC<{
  ariaLabel: string;
  disabled: boolean;
  onClick: () => void;
  children: (color: string) => React.ReactNode;
}> = ({ ariaLabel, disabled, onClick, children }) => (
  <button
    type="button"
    aria-label={ariaLabel}
    title={ariaLabel}
    disabled={disabled}
    onClick={onClick}
    style={{
      width: '24px',
      height: '24px',
      padding: 0,
      border: 'none',
      background: 'transparent',
      cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: disabled ? 0.5 : 1,
    }}
  >
    {children(disabled ? TEXT_INACTIVE : TEXT_ACTIVE)}
  </button>
);

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onChange,
  showFirstLast = true,
  style,
}) => {
  const lastPage = Math.max(1, totalPages);
  const isFirst = currentPage <= 1;
  const isLast = currentPage >= lastPage;
  const goTo = (page: number) => onChange(Math.min(lastPage, Math.max(1, page)));

  return (
    <nav
      aria-label="쪽 이동"
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px',
        ...style,
      }}
    >
      {showFirstLast && (
        <ArrowButton ariaLabel="첫 쪽" disabled={isFirst} onClick={() => goTo(1)}>
          {(color) => <LeftArrowDoubleIcon width={24} height={24} color={color} />}
        </ArrowButton>
      )}
      <ArrowButton ariaLabel="이전 쪽" disabled={isFirst} onClick={() => goTo(currentPage - 1)}>
        {(color) => <LeftArrowIcon width={24} height={24} color={color} />}
      </ArrowButton>

      {Array.from({ length: lastPage }, (_, index) => index + 1).map((page) => {
        const isCurrent = page === currentPage;
        return (
          <button
            key={page}
            type="button"
            aria-label={`${page}쪽`}
            aria-current={isCurrent ? 'page' : undefined}
            onClick={() => goTo(page)}
            style={{
              minWidth: '24px',
              height: '24px',
              padding: '0 2px',
              borderRadius: '8px',
              border: 'none',
              background: 'transparent',
              color: isCurrent ? TEXT_ACTIVE : TEXT_INACTIVE,
              fontSize: '14px',
              fontFamily: 'inherit',
              fontWeight: 500,
              lineHeight: '21px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {page}
          </button>
        );
      })}

      <ArrowButton ariaLabel="다음 쪽" disabled={isLast} onClick={() => goTo(currentPage + 1)}>
        {(color) => <RightArrowIcon width={24} height={24} color={color} />}
      </ArrowButton>
      {showFirstLast && (
        <ArrowButton ariaLabel="마지막 쪽" disabled={isLast} onClick={() => goTo(lastPage)}>
          {(color) => <RightArrowDoubleIcon width={24} height={24} color={color} />}
        </ArrowButton>
      )}
    </nav>
  );
};

export default Pagination;
